<script setup lang="ts">
import {reactive, ref} from "vue";
import {dayjs, ElNotification} from "element-plus";
import {blobToFile, durationList} from "@/utils/common.ts";
import SingleImageUploader from "components/common/SingleImageUploader.vue";
import {CourseBasicInfo, CourseCate, CourseSubCate, GroupCourseDiscount, SaveCourse} from "@/types";
import MultiImageUploader from "components/common/MultiImageUploader.vue";
import html2canvas from "html2canvas";
import {uploadImage} from "@/utils/cos.ts";
import {ProgressInfo} from "cos-js-sdk-v5";
import {SystemService} from "@/api/api.ts";

const cateList = ref<CourseCate[]>([])
const twoCateList = ref<CourseSubCate[]>()
const imgContainer = ref<HTMLDivElement>();
const step = ref(1)

let courseStatus = ref(2)

// 上下架时间
const startDate = ref<Date>()
const endDate = ref<Date>()
// 有效类型
const validType = ref(2)
// 截止日期
const untilDate = ref('')
// 固定天数
const constDate = ref(365)
// 是否开放试看
const is_try = ref(false)
// 是否不限定购买
const not_sign = ref(false)

const form = reactive({
  type: 0,
  // 标题
  title: "",
  // 简介
  short_detail: "",
  // 每讲时长（分钟），直播课时必传
  duration: 45,
  // 组合课优惠描述，组合课必传
  sign_time: "",
  // 课程价格（组合课时可不传）
  price: "",
  // 优惠信息；如：[{“count”:1,”discount”:0.9},{“count”:3,”discount”:0.8}]
  describeInfo: [{count: 2, discount: 9.5}] as GroupCourseDiscount[],
  // 分类标签id
  cate_id: undefined as (number | undefined),
  // 二级分类标签id
  two_cate_id: undefined as (number | undefined),
})

const initCate = async () => {
  try {
    const res = await SystemService.getCourseCateList({})
    if (res.data.code == 200) {
      cateList.value = res.data.data || [];
    }
  } catch (err) {
    console.warn(err)
  }
}

const init = async (info: CourseBasicInfo) => {
  await initCate()
  form.cate_id = info.cate_id
  onCateChange(info.cate_id)
  courseStatus.value = info.status
  form.type = info.type
  form.title = info.title
  form.short_detail = info.short_detail
  form.duration = info.duration
  form.sign_time = info.sign_time
  form.price = info.price
  form.describeInfo = info.describe_info
  form.two_cate_id = info.two_cate_id
  if (info.status === 1) {
    startDate.value = dayjs(info.putaway_time * 1000).toDate()
    endDate.value = dayjs(info.down_time * 1000).toDate()
    is_try.value = info.is_try === 1
    not_sign.value = info.is_sign === 0
    if (info.buy_valid_date ) {
      if (info.buy_valid_date === 1) {
        validType.value = 1
      } else if (info.buy_valid_date > 1000000000) {
        validType.value = 2
        untilDate.value = dayjs(info.buy_valid_date * 1000).format('YYYY-MM-DD')
      } else {
        validType.value = 3
        constDate.value = info.buy_valid_date
      }
    }
  }
  if (info.thumb_image) {
    thumbImageUploader.value?.init(info.thumb_image)
  } else {
    thumbImageUploader.value?.init('')
  }
  if (info.content) {
    multiImageUploader.value?.init(info.content.split(","))
  } else {
    multiImageUploader.value?.init([])
  }
}

const onCateChange = (cateId: any) => {
  for (let i = 0; i < cateList.value.length; i++) {
    if (cateList.value[i].id === cateId) {
      form.two_cate_id = undefined
      twoCateList.value = cateList.value[i].two_list || []
      break
    }
  }
}

const thumbImageUploader = ref<InstanceType<typeof SingleImageUploader>>()
const multiImageUploader = ref<InstanceType<typeof MultiImageUploader>>()

const emit = defineEmits(["onEditCourse"])

const onSubmit = () => {
  const info = genData()
  console.log(info)
  if (info) {
    emit("onEditCourse", info)
  }
}

// 表单数据生成
const genData = (): SaveCourse | undefined => {
  let info: SaveCourse = Object.assign({})
  if (courseStatus.value === 1) {
    if (!startDate.value) {
      ElNotification.warning({
        message: '请选择课程上架时间',
      })
      return undefined
    }
    if (!endDate.value) {
      ElNotification.warning({
        message: '请选择课程下架时间',
      })
      return undefined
    }
    let start_time = dayjs(startDate.value).startOf('day').unix()
    let end_time = dayjs(endDate.value).startOf('day').unix()
    if (!start_time || !end_time) {
      ElNotification.warning({
        message: '请选择课程上架时间',
      })
      return undefined
    }
    if (start_time >= end_time) {
      ElNotification.warning({
        message: '课程上架时间不能大于等于课程下架时间',
      })
      return undefined
    }
    if (!validate()) {
      return undefined
    }
    info.putaway_time = start_time
    info.down_time = end_time
    info.buy_valid_date = calValidDate()!!
    info.is_try = is_try.value ? 1 : 0
    info.is_sign = !not_sign.value ? 1 : 0
  }
  if (form.type === 1) {
    if (genLiveCourseData(info)) {
      return info
    }
  } else if (form.type === 2) {
    if (genRecordCourseData(info)) {
      return info
    }
  } else if (form.type === 3) {
    if (genSeriesCourseData(info)) {
      return info
    }
  } else if (form.type === 4) {
    if (genGroupCourseData(info)) {
      return info
    }
  }
  return undefined;
}

// 直播课表单数据生成
const genLiveCourseData = (info: SaveCourse): boolean => {
  info.type = 1
  info.title = form.title
  info.cate_id = form.cate_id
  info.two_cate_id = form.two_cate_id
  info.sign_time = form.sign_time
  info.duration = form.duration
  info.short_detail = form.short_detail
  const images = []
  if (multiImageUploader.value?.fileList) {
    const list = multiImageUploader.value?.fileList
    for (let i = 0; i < list.length; i++) {
      if (list[i].status === 'success') {
        images.push(list[i].url)
      } else if (list[i].status === 'uploading') {
        ElNotification.warning({
          title: "提醒",
          message: "当前简介图片正在上传中, 请稍后"
        })
        return false
      } else if (list[i].status === 'fail') {
        ElNotification.warning({
          title: "提醒",
          message: "部分简介图片上传失败，请删除或重新上传"
        })
        return false
      }
    }
  }
  info.content = images.join(",")
  let thumb = ""
  if (thumbImageUploader.value?.fileList) {
    const list = thumbImageUploader.value?.fileList
    if (list.length > 0) {
      if (list[0].status === 'success') {
        thumb = list[0].url || ''
      } else if (list[0].status === 'uploading') {
        ElNotification.warning({
          title: "提醒",
          message: "当前封面图片正在上传中, 请稍后"
        })
        return false
      } else if (list[0].status === 'fail') {
        ElNotification.warning({
          title: "提醒",
          message: "当前封面图片上传失败，请删除或重新上传"
        })
        return false
      }
    }
  }
  info.thumb_image = thumb
  info.price = form.price
  return true
}

// 录播课表单数据生成
const genRecordCourseData = (info: SaveCourse): boolean => {
  info.type = 2
  info.title = form.title
  info.cate_id = form.cate_id
  info.two_cate_id = form.two_cate_id
  info.short_detail = form.short_detail
  const images = []
  if (multiImageUploader.value?.fileList) {
    const list = multiImageUploader.value?.fileList
    for (let i = 0; i < list.length; i++) {
      if (list[i].status === 'success') {
        images.push(list[i].url)
      } else if (list[i].status === 'uploading') {
        ElNotification.warning({
          title: "提醒",
          message: "当前简介图片正在上传中, 请稍后"
        })
        return false
      } else if (list[i].status === 'fail') {
        ElNotification.warning({
          title: "提醒",
          message: "部分简介图片上传失败，请删除或重新上传"
        })
        return false
      }
    }
  }
  info.content = images.join(",")
  let thumb = ""
  if (thumbImageUploader.value?.fileList) {
    const list = thumbImageUploader.value?.fileList
    if (list.length > 0) {
      if (list[0].status === 'success') {
        thumb = list[0].url || ''
      } else if (list[0].status === 'uploading') {
        ElNotification.warning({
          title: "提醒",
          message: "当前封面图片正在上传中, 请稍后"
        })
        return false
      } else if (list[0].status === 'fail') {
        ElNotification.warning({
          title: "提醒",
          message: "当前封面图片上传失败，请删除或重新上传"
        })
        return false
      }
    }
  }
  info.thumb_image = thumb
  info.price = form.price
  return true
}

// 系列课表单数据生成
const genSeriesCourseData = (info: SaveCourse): boolean => {
  info.type = 3
  info.title = form.title
  info.cate_id = form.cate_id
  info.two_cate_id = form.two_cate_id
  info.short_detail = form.short_detail
  const images = []
  if (multiImageUploader.value?.fileList) {
    const list = multiImageUploader.value?.fileList
    for (let i = 0; i < list.length; i++) {
      if (list[i].status === 'success') {
        images.push(list[i].url)
      } else if (list[i].status === 'uploading') {
        ElNotification.warning({
          title: "提醒",
          message: "当前简介图片正在上传中, 请稍后"
        })
        return false
      } else if (list[i].status === 'fail') {
        ElNotification.warning({
          title: "提醒",
          message: "部分简介图片上传失败，请删除或重新上传"
        })
        return false
      }
    }
  }
  info.content = images.join(",")
  let thumb = ""
  if (thumbImageUploader.value?.fileList) {
    const list = thumbImageUploader.value?.fileList
    if (list.length > 0) {
      if (list[0].status === 'success') {
        thumb = list[0].url || ''
      } else if (list[0].status === 'uploading') {
        ElNotification.warning({
          title: "提醒",
          message: "当前封面图片正在上传中, 请稍后"
        })
        return false
      } else if (list[0].status === 'fail') {
        ElNotification.warning({
          title: "提醒",
          message: "当前封面图片上传失败，请删除或重新上传"
        })
        return false
      }
    }
  }
  info.thumb_image = thumb
  info.price = form.price
  return true
}

// 组合课表单数据生成
const genGroupCourseData = (info: SaveCourse): boolean => {
  info.type = 4
  info.title = form.title
  info.cate_id = form.cate_id
  info.two_cate_id = form.two_cate_id
  info.short_detail = form.short_detail
  const images = []
  if (multiImageUploader.value?.fileList) {
    const list = multiImageUploader.value?.fileList
    for (let i = 0; i < list.length; i++) {
      if (list[i].status === 'success') {
        images.push(list[i].url)
      } else if (list[i].status === 'uploading') {
        ElNotification.warning({
          title: "提醒",
          message: "当前简介图片正在上传中, 请稍后"
        })
        return false
      } else if (list[i].status === 'fail') {
        ElNotification.warning({
          title: "提醒",
          message: "部分简介图片上传失败，请删除或重新上传"
        })
        return false
      }
    }
  }
  info.content = images.join(",")
  let thumb = ""
  if (thumbImageUploader.value?.fileList) {
    const list = thumbImageUploader.value?.fileList
    if (list.length > 0) {
      if (list[0].status === 'success') {
        thumb = list[0].url || ''
      } else if (list[0].status === 'uploading') {
        ElNotification.warning({
          title: "提醒",
          message: "当前封面图片正在上传中, 请稍后"
        })
        return false
      } else if (list[0].status === 'fail') {
        ElNotification.warning({
          title: "提醒",
          message: "当前封面图片上传失败，请删除或重新上传"
        })
        return false
      }
    }
  }
  info.thumb_image = thumb
  info.price = "0"
  return true
}

const genThumb = async () => {
  const canvas = await html2canvas(imgContainer.value!!);
  canvas.toBlob(
      async blob => {
        const newFile = blobToFile(blob!!, "thumb.png");
        uploadImage(newFile, {
          onTaskReady: (taskId: string) => {
            console.log(taskId)
          },
          onProgress: (progressData: ProgressInfo) => {
            console.log(progressData.percent)
          },
          onUploadFail: (err: string) => {
            console.log(err)
          },
          onFileFinish: (url: string) => {
            console.log(url)
            thumbImageUploader.value?.init(url)
          }
        })
      },
      "image/png",
      1
  );
}

const calValidDate = (): number | undefined => {
  if (validType.value === 1) {
    // 永久
    return 1
  } else if (validType.value === 2) {
    // 截止日期
    if (untilDate.value) {
      return dayjs(untilDate.value).startOf('day').unix()
    } else {
      return undefined
    }
  } else if (validType.value === 3) {
    return constDate.value
  }
  return undefined
}

const validate = () => {
  if (validType.value === 2) {
    // 截止日期
    if (!untilDate.value) {
      ElNotification.error({
        message: '请填写购课后有效期',
      })
      return false
    }
  } else if (validType.value === 3) {
    if (constDate.value < 3) {
      ElNotification.error({
        message: '购买后有效期不能少于3天',
      })
      return false
    }
  }
  return true
}

const reset = () => {
  step.value = 1
  courseStatus.value = 2
  startDate.value = undefined
  endDate.value = undefined
  validType.value = 2
  untilDate.value = ''
  constDate.value = 365
  is_try.value = false
  not_sign.value = false
}

defineExpose({
  init, reset
})
</script>

<template>
  <el-form :model="form" label-width="auto">
    <el-form-item label="课程标题" v-if="step === 1">
      <el-input v-model="form.title" placeholder="请输入课程标题"/>
    </el-form-item>
    <el-form-item label="课程分类" v-if="step === 1">
      <el-select v-model="form.cate_id"  @change="onCateChange">
        <el-option v-for="item in cateList" :label="item.name" :value="item.id"></el-option>
      </el-select>
    </el-form-item>
    <el-form-item label="二级分类">
      <el-select v-model="form.two_cate_id" placeholder="选择子分类(可选)">
        <el-option v-for="item in twoCateList" :label="item.name" :value="item.id"></el-option>
      </el-select>
    </el-form-item>
    <el-form-item label="课程封面" v-show="step === 1">
      <single-image-uploader ref="thumbImageUploader" @on-generate="genThumb"></single-image-uploader>
    </el-form-item>
    <el-form-item label="课程简介" v-if="step === 1">
      <el-input v-model="form.short_detail" type="textarea"
                placeholder="请输入课程简介(文字描述与图片至少填一项)"></el-input>
    </el-form-item>
    <el-form-item label=" " v-show="step === 1">
      <multi-image-uploader :max="3" ref="multiImageUploader"/>
    </el-form-item>
    <el-form-item label="每讲时长" v-if="form.type === 1 && step === 1">
      <div class="flex">
        <el-select v-model="form.duration" style="width: 10rem">
          <el-option v-for="item in durationList()" :label="item" :value="item">
          </el-option>
        </el-select>
        <div class="ml-1rem">分钟</div>
      </div>
    </el-form-item>
    <el-form-item label="上课时间" v-if="form.type === 1 && step === 1">
      <el-input v-model="form.sign_time" placeholder="请输入上课时间, 如：每周六晚九点上课、详情见课表安排等"/>
    </el-form-item>
    <el-form-item label="价格" v-if="step === 1 && form.type !== 4">
      <div class="flex">
        <el-input
            v-model="form.price" placeholder="请输入课程价格（填0即免费）"
            style="width: 20rem"
            :disabled="courseStatus === 1"
            />
        <div class="ml-1rem">元</div>
      </div>
    </el-form-item>
    <el-form-item label="课程上下架时间" v-if="courseStatus === 1 && step === 2">
      <el-date-picker v-model="startDate" type="date" placeholder="课程上架日期">
      </el-date-picker>
      <span class="mx-.5rem">
        ~
      </span>
      <el-date-picker v-model="endDate" type="date" placeholder="课程下架日期">
      </el-date-picker>
    </el-form-item>
    <el-form-item label="购买后有效期" v-if="courseStatus === 1 && step === 2">
      <el-radio-group v-model="validType" style="flex-direction: column; align-items: flex-start">
        <!--  暂时隐藏永久有效     -->
        <!--  <el-radio :value="1">永久有效</el-radio> -->
        <el-radio :value="2" class="mt-.5rem">
          <el-date-picker type="date" placeholder="截至日期" v-model="untilDate"></el-date-picker>
          前有效
        </el-radio>
        <el-radio :value="3" class="mt-.5rem">
          购买后
          <el-input type="number" v-model="constDate"></el-input>
          天有效
        </el-radio>
      </el-radio-group>
    </el-form-item>
    <el-form-item label="是否开放试看" v-if="form.type === 2 && courseStatus === 1 && step === 2">
      <el-checkbox v-model="is_try" label="第一讲免费试看" size="large" class="mt-[-3px]"/>
    </el-form-item>
    <el-form-item label="是否限定购买" v-if="courseStatus === 1 && step === 2">
      <el-checkbox v-model="not_sign" label="限定购买(勾选此项则只能老师添加学员)" size="large" class="mt-[-3px]"/>
    </el-form-item>
    <div class="flex items-end justify-end">
      <el-button type="primary" v-if="courseStatus === 1" plain @click="step == 1 ? step = 2 : step = 1">
        {{ step === 1 ? '下一步' : '上一步' }}
      </el-button>
      <el-button type="primary" class="" @click="onSubmit" v-if="courseStatus === 2 || step === 2">确认修改</el-button>
    </div>
  </el-form>
  <div class="img-preview-large" ref="imgContainer">
    {{ form.title }}
  </div>
</template>

<style scoped>
.img-preview-large {
  background-image: url("@/assets/image/thumb_bg.png");
  background-repeat: no-repeat;
  background-size: 750px 422px;
  -moz-background-size: 100% 100%;
  width: 750px;
  height: calc(750px * 9 / 16);
  color: white;
  font-weight: bold;
  font-size: 50px;
  padding: 96px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  line-height: 1.3;
  position: absolute;
  top: -9999px;
  left: -9999px;
}
</style>
